import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { m } from '@/lib/motion';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { fadeIn, slideInUp } from '@/lib/motion-variants';
import { validateEmail } from '@/utils/validateEmail';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { Input } from '@/components/ui/Input';
import { LanguageSelector } from '@/components/ui/LanguageSelector';
import { useCurrentYear } from '@/hooks/useCurrentYear';

interface LoginFormData {
  email: string;
  password: string;
}

interface LoginScreenProps {
  onLogin?: (credentials: LoginFormData) => Promise<void>;
  onForgotPassword?: () => void;
  isLoading?: boolean;
}

export function LoginScreen({ onLogin, onForgotPassword, isLoading = false }: LoginScreenProps) {
  const { t } = useTranslation();
  const year = useCurrentYear();
  const emailInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errorFields, setErrorFields] = useState<{
    email?: 'required' | 'invalid';
    password?: 'required';
  }>({});

  const errors = {
    email:
      errorFields.email === 'required'
        ? t('login.emailRequired')
        : errorFields.email === 'invalid'
          ? t('login.emailInvalid')
          : undefined,
    password: errorFields.password === 'required' ? t('login.passwordRequired') : undefined,
  };

  const validateForm = (): boolean => {
    const next: typeof errorFields = {};

    if (!formData.email) {
      next.email = 'required';
    } else if (!validateEmail(formData.email)) {
      next.email = 'invalid';
    }

    if (!formData.password) {
      next.password = 'required';
    }

    setErrorFields(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onLogin?.(formData);
    } catch {
      // Errors are surfaced by the auth layer (e.g. toast). Avoid unhandled promise rejections.
    }
  };

  const handleInputChange = (field: keyof LoginFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errorFields[field]) {
      setErrorFields((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  useEffect(() => {
    if (isLoading) return;
    emailInputRef.current?.focus();
  }, [isLoading]);

  return (
    <main className='relative min-h-screen bg-gradient-to-br from-dark-500 via-dark-500 to-dark-600 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center p-4 sm:items-center items-start pt-16 sm:pt-4'>
      <div className='absolute top-4 right-4 z-10'>
        <LanguageSelector variant='dark' compact />
      </div>
      <div className='w-full max-w-md'>
        {/* Logo */}
        <m.div {...fadeIn} className='text-center mb-10'>
          <img src='/xkey-clean.png' alt='Xkey logo' height='60' width='108' className='mx-auto mb-6 drop-shadow-lg' />
        </m.div>

        {/* Login Form */}
        <m.div
          {...slideInUp}
          className='bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-xl shadow-2xl border border-gray-200/20 dark:border-gray-700/30 p-8 md:p-10'
        >
          <div className='text-center mb-8'>
            <h2 className='text-3xl font-bold text-foreground mb-2'>{t('login.header')}</h2>
            <div className='h-1 w-16 bg-primary-500 mx-auto rounded-full mt-3' />
          </div>

          <form onSubmit={handleSubmit} noValidate className='space-y-5'>
            <div className='space-y-1'>
              <Input
                label={t('login.email')}
                type='email'
                placeholder={t('login.email')}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                leftIcon={<Mail className='h-4 w-4' />}
                error={errors.email}
                disabled={isLoading}
                className='bg-gray-50 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600 focus:bg-white dark:focus:bg-gray-700 transition-all duration-200'
                required
                ref={emailInputRef}
              />
            </div>

            <div className='space-y-1'>
              <Input
                label={t('login.password')}
                type={showPassword ? 'text' : 'password'}
                placeholder={t('login.password')}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                leftIcon={<Lock className='h-4 w-4' />}
                rightIcon={
                  <IconButton
                    type='button'
                    size='icon-sm'
                    tone='inputAdornment'
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                  >
                    {showPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                  </IconButton>
                }
                error={errors.password}
                disabled={isLoading}
                className='bg-gray-50 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600 focus:bg-white dark:focus:bg-gray-700 transition-all duration-200'
                required
              />
            </div>

            <Button
              type='submit'
              className='w-full mt-6 h-12 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200'
              loading={isLoading}
              disabled={isLoading}
            >
              {t('login.login')}
            </Button>
          </form>

          {/* Forgot Password Link */}
          {onForgotPassword && (
            <div className='mt-6 text-center'>
              <Button
                type='button'
                variant='link'
                onClick={onForgotPassword}
                disabled={isLoading}
                className='h-auto p-0 text-sm font-medium text-primary-500 dark:text-primary-400 hover:text-primary-600 dark:hover:text-primary-300 hover:scale-100 active:scale-100 hover:shadow-none'
              >
                {t('general.forgotPassword')}
              </Button>
            </div>
          )}
        </m.div>

        {/* Footer */}
        <div className='text-center mt-8 text-sm text-white/70'>
          <p>{year != null ? t('general.copyright', { year }) : null}</p>
          <p className='mt-2 flex items-center justify-center gap-3'>
            <Link to='/tietosuojaseloste' className='underline underline-offset-2 hover:text-white'>
              {t('privacyPolicy.header')}
            </Link>
            <a
              href='https://tawk.to/chat/665837c7981b6c5647766164/1hv49jpcp'
              target='_blank'
              rel='noopener noreferrer'
              className='underline underline-offset-2 hover:text-white'
            >
              {t('customerService.header')}
            </a>
          </p>
        </div>
      </div>
    </main>
  );
}
